import { Link } from 'react-router-dom'
import { ArrowRight, Check, ShieldCheck } from 'lucide-react'

import { brandifyNode } from '@/components/brand/kindlumen'
import { JsonLd } from '@/components/seo/json-ld'
import { SeoHead } from '@/components/seo/seo-head'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardTitle } from '@/components/ui/card'
import { SectionHeading } from '@/components/ui/section-heading'
import { DONOR_COVERAGE_STAT, FREE_PLAN, PRICING_FAQ } from '@/data/pricing-content'
import { createMetadata, getFaqSchema, getProductSchema, marketingSeoPages } from '@/lib/seo'

// The three numbers that carry the whole page. Shown as figures rather than a
// bullet list — "$0" reads instantly in a way "No monthly fee" does not.
const zeroCosts = [
  { amount: '$0', label: 'Setup fee' },
  { amount: '$0', label: 'Monthly fee' },
  { amount: '$0', label: 'Platform fee' },
] as const

// A $100 gift, both ways. Figures come from the same calculation the checkout
// uses (calculateDonationCharge at Stripe's standard 2.9% + 30¢).
const donationScenarios = [
  {
    title: 'Donor covers transaction costs',
    caption: 'They add the processing cost so your gift arrives whole.',
    featured: true,
    rows: [
      { label: 'Donor pays', value: '$104.33' },
      { label: 'Stripe processing', value: '−$3.33' },
      { label: 'KindLumen (1%)', value: '−$1.00' },
    ],
    total: { label: 'You receive', value: '$100.00' },
  },
  {
    title: 'Donor does not cover them',
    caption: "Stripe's fee comes out of the gift. KindLumen still takes nothing.",
    featured: false,
    rows: [
      { label: 'Donor pays', value: '$100.00' },
      { label: 'Stripe processing', value: '−$3.20' },
      { label: 'KindLumen', value: '$0.00' },
    ],
    total: { label: 'You receive', value: '$96.80' },
  },
] as const

const securitySignals = [
  'Powered by Stripe — PCI-DSS Level 1 payment infrastructure',
  'Money never touches KindLumen — donations go straight to your own account',
  'Card details are entered through Stripe-hosted payment fields',
] as const

const metadata = createMetadata(marketingSeoPages.pricing)

export default function PricingPage() {
  return (
    <div className="pb-20">
      <SeoHead metadata={metadata} />
      <JsonLd data={getProductSchema()} />
      <JsonLd data={getFaqSchema([...PRICING_FAQ])} />

      {/* Hero — one claim, stated as plainly and as large as possible. */}
      <section className="relative overflow-hidden bg-night">
        <div className="pointer-events-none absolute inset-0 bg-hero-glow opacity-90" aria-hidden="true" />
        <div className="pointer-events-none absolute -left-24 top-1/3 h-72 w-72 rounded-full bg-brand/25 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -right-16 top-10 h-64 w-64 rounded-full bg-sky/20 blur-3xl" aria-hidden="true" />

        <div className="marketing-shell relative py-24 text-center sm:py-32">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white/80">
            Pricing
          </div>

          {/* Fluid size, tuned to Poppins 600 with the web font loaded (the
              fallback font is ~15% narrower, so never measure against it): the
              longer line, "Free Stripe donation forms.", is 13.7x the font size.
              From sm up 6vw keeps each line whole at every width and the 5rem
              cap keeps it inside the 72rem box on wide screens. On phones 9.3vw
              lets each line break once ("Your organization" / "pays $0."). It
              wraps rather than nowrap on purpose: a nowrap line that outgrows
              the box is clipped by the section's overflow-hidden with no
              visible error, which is the bug this replaced. */}
          <h1 className="mx-auto mt-8 max-w-6xl text-balance text-[length:clamp(1.75rem,9.3vw,2.5rem)] font-semibold leading-[1] text-white sm:text-[length:clamp(2.5rem,6vw,5rem)] sm:leading-[0.98]">
            Free Stripe donation forms.
            {/* pb/-mb: background-clip:text paints only inside the box, and at this
                tight leading the box ends above the descenders, which cut the
                tails off "g", "y" and "p". The padding extends the paint area and
                the negative margin gives the space back. */}
            <span className="-mb-[0.2em] mt-3 block bg-[linear-gradient(120deg,#7DD3FC,#A5B4FC_50%,#6EE7B7)] bg-clip-text pb-[0.2em] text-transparent">
              Your organization pays $0.
            </span>
          </h1>

          <p className="mx-auto mt-8 max-w-2xl text-lg leading-8 text-white/70 sm:text-xl sm:leading-9">
            {brandifyNode(
              'KindLumen is a free Stripe donation form. No subscription fees. No setup fees. No platform fees. Donors can choose to cover transaction costs, so your organization receives the full amount they intended to give.',
              'inverse',
            )}
          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg">
              <Link to={FREE_PLAN.cta.href}>{FREE_PLAN.cta.label}</Link>
            </Button>
            <Button asChild size="lg" variant="dark">
              <Link to="#the-math">
                See the math
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>

          <div className="mx-auto mt-16 grid max-w-3xl gap-4 sm:grid-cols-3">
            {zeroCosts.map((item) => (
              <div
                key={item.label}
                className="rounded-[24px] border border-white/10 bg-white/[0.07] px-6 py-8 backdrop-blur-sm"
              >
                <p className="text-5xl font-semibold leading-none text-white">{item.amount}</p>
                <p className="mt-3 text-sm font-medium text-white/60">{item.label}</p>
              </div>
            ))}
          </div>

          <p className="mt-10 text-sm text-white/50">
            {brandifyNode('Unlimited forms, campaigns, and donations — no response caps. No contracts, no sales calls.', 'inverse')}
          </p>
        </div>
      </section>

      {/* The math. Every claim above resolves here, in actual dollars.
          z-10 keeps the panel above the hero it deliberately overlaps. */}
      <section id="the-math" className="marketing-shell relative z-10 -mt-16 sm:-mt-20">
        <div className="panel p-8 sm:p-10 lg:p-12">
          <div className="max-w-2xl">
            <div className="eyebrow">The math</div>
            <h2 className="mt-5 text-balance text-3xl font-semibold leading-[1.12] sm:text-[2.6rem]">
              So how does a $100 donation actually land?
            </h2>
            <p className="mt-4 text-base leading-7 text-ink-soft sm:text-lg sm:leading-8">
              {brandifyNode(
                'Card processing costs do not disappear. A donor can add enough to cover them; otherwise Stripe deducts its fee from the gift.',
              )}
            </p>
          </div>

          <div className="mt-10 grid gap-5 lg:grid-cols-2">
            {donationScenarios.map((scenario) => (
              <div
                key={scenario.title}
                className={
                  scenario.featured
                    ? 'relative rounded-[26px] border-2 border-brand bg-[linear-gradient(180deg,rgba(37,99,235,0.06),transparent)] p-7 shadow-soft'
                    : 'rounded-[26px] border border-line bg-surface-strong p-7'
                }
              >
                {scenario.featured ? (
                  <span className="absolute -top-3 left-7 rounded-full bg-brand px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-white">
                    {DONOR_COVERAGE_STAT.verified ? 'Most common' : 'Recommended'}
                  </span>
                ) : null}

                <h3 className="text-lg font-semibold text-ink">{scenario.title}</h3>
                <p className="mt-2 text-sm leading-6 text-ink-muted">{brandifyNode(scenario.caption)}</p>

                <dl className="mt-7 space-y-3">
                  {scenario.rows.map((row) => (
                    <div key={row.label} className="flex items-baseline justify-between gap-4">
                      <dt className="text-sm text-ink-soft">{brandifyNode(row.label)}</dt>
                      <dd className="font-mono text-sm tabular-nums text-ink-soft">{row.value}</dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-5 flex items-baseline justify-between gap-4 border-t border-line pt-5">
                  <span className="text-sm font-semibold text-ink">{scenario.total.label}</span>
                  <span
                    className={
                      scenario.featured
                        ? 'font-mono text-3xl font-semibold tabular-nums text-brand'
                        : 'font-mono text-3xl font-semibold tabular-nums text-ink'
                    }
                  >
                    {scenario.total.value}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-8 text-sm leading-6 text-ink-muted">
            {DONOR_COVERAGE_STAT.statement} The example uses Stripe&apos;s published US standard rate of 2.9% + 30¢
            for a successful domestic card payment, reviewed September 3, 2026.{' '}
            <a
              href="https://stripe.com/pricing"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-brand hover:underline"
            >
              Check Stripe&apos;s current pricing
            </a>{' '}
            for international cards, currency conversion, other payment methods, or custom rates.
          </p>
        </div>
      </section>

      <section className="marketing-shell mt-20 sm:mt-24">
        <div className="grid gap-6 xl:grid-cols-[1.04fr_0.96fr]">
          <div className="panel p-8 lg:p-10">
            <SectionHeading eyebrow="Trust and security" title="Secure infrastructure." />

            <div className="mt-8 grid gap-4">
              {securitySignals.map((item) => (
                <div key={item} className="flex items-start gap-3 rounded-[22px] border border-line bg-surface-strong px-4 py-4">
                  <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand/10">
                    <ShieldCheck className="h-3.5 w-3.5 text-brand" />
                  </div>
                  <p className="text-sm leading-6 text-ink-soft">{brandifyNode(item)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="panel-dark overflow-hidden p-8 lg:p-10">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold text-white/80">
              <Check className="h-3.5 w-3.5" />
              Setup guide
            </div>
            <h2 className="mt-6 max-w-lg text-3xl font-semibold leading-[1.16] text-white sm:text-[2.2rem]">
              Review the workflow before launch.
            </h2>
            <p className="mt-4 max-w-xl text-base leading-7 text-white/70">
              See how Stripe connection, form setup, embeds, transaction-cost coverage, and donation records work together.
            </p>

            <div className="mt-8">
              <Button asChild size="lg" variant="dark">
                <Link to="/docs">
                  Read the documentation
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-shell mt-20 sm:mt-24">
        <SectionHeading eyebrow="FAQ" title="Pricing questions, answered." />

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          {PRICING_FAQ.map((item) => (
            <Card key={item.question} className="h-full">
              <CardTitle className="text-xl">{brandifyNode(item.question)}</CardTitle>
              <CardDescription className="mt-4">{brandifyNode(item.answer)}</CardDescription>
            </Card>
          ))}
        </div>
      </section>

      <section className="marketing-shell mt-20 sm:mt-24">
        <div className="overflow-hidden rounded-[30px] border border-white/10 bg-night p-8 text-center text-white shadow-glow sm:p-14">
          <h2 className="mx-auto max-w-2xl text-balance text-4xl font-semibold leading-[1.14] text-white sm:text-5xl">
            Start accepting donations today.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-7 text-white/70">
            {brandifyNode('No setup, subscription, or platform fee from KindLumen.', 'inverse')}
          </p>
          <div className="mt-8 flex justify-center">
            <Button asChild size="lg">
              <Link to={FREE_PLAN.cta.href}>{FREE_PLAN.cta.label}</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}

export const Component = PricingPage
